import { useCallback, useEffect, useMemo, useState } from 'react';
import { ModelEditorFactory, ModelFactory } from '@leverege/ui-plugin';
import { useStateOnChange } from '@leverege/ui-hooks';
import isEqual from 'fast-deep-equal';
import { GroupDelegate } from '@leverege/molten-core';
import ConditionFilterOptionsCreators from "./ConditionFilterOptionsCreators.js";
import FilterSourcesModel from "../FilterSourcesModel.js";
import { EMPTY_FILTER_SOURCE } from "../../dataViewers/Util.js";
import ConditionFilterModelInitializer from "./ConditionFilterModelInitializer.js";
import FilterSortMetadataModel from "./FilterSortMetadataModel.js";
import FilterSourceModel from "../FilterSourceModel.js";
export default function useConditionFilterActionRenderer(opts) {
  const {
    context
  } = opts;
  const {
    clientProps,
    mobile,
    relationship,
    filters,
    filterName = 'filterBar',
    objectType,
    onFilterChange
  } = context;
  const {
    delegate
  } = clientProps;
  const value = useMemo(() => {
    const filter = FilterSourcesModel.getSource(filters, filterName) || EMPTY_FILTER_SOURCE;
    return filter?.metadata?.filter || ConditionFilterModelInitializer.create(delegate.type, objectType);
  }, [filters, filterName, delegate?.type, objectType]);
  const [sValue, onChange] = useStateOnChange(value);
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    const sConverted = delegate?.convertToFilter(sValue, context);
    const converted = delegate?.convertToFilter(value, context);
    const equal = isEqual(sConverted, converted);
    if (!equal) {
      const filter = FilterSourcesModel.getSource(filters, filterName) || EMPTY_FILTER_SOURCE;
      const conditionFilter = sConverted || null;
      const metadata = FilterSortMetadataModel.setFilter(filter.metadata || {}, sValue);
      let filterModel = FilterSourceModel.setMetadata(filter, metadata);
      filterModel = FilterSourceModel.setFilter(filterModel, conditionFilter);
      onFilterChange({
        data: filterName,
        value: filterModel
      });
    }
  }, [sValue]);
  useEffect(() => {
    const sConverted = delegate?.convertToFilter(sValue, context);
    const converted = delegate?.convertToFilter(value, context);
    if (!isEqual(sConverted, converted)) {
      onChange({
        value
      });
    }
  }, [value]);
  const onPopupChange = useCallback(() => {
    setOpened(!opened);
  }, [opened]);
  const editor = useMemo(() => {
    return ModelEditorFactory.create(sValue, {
      context,
      clientProps,
      delegate,
      relationship,
      onChange
    });
  }, [clientProps, context, delegate, onChange, relationship, sValue]);
  const conditionCount = useMemo(() => {
    const metadatas = FilterSourcesModel.getMetadata(filters);
    const filterBarFilterMetadata = metadatas?.[filterName]?.filter;
    if (delegate.type === GroupDelegate.TYPE) {
      const f = delegate.convertToFilter(filterBarFilterMetadata, context);
      return f?.conditions?.length || 0;
    }
    return ModelFactory.get(filterBarFilterMetadata)?.getConditionCount?.() || 0;
  }, [filters, context, delegate, filterName]);
  const optionsCreator = useMemo(() => {
    return ConditionFilterOptionsCreators.getOptionsCreatorFor(delegate.type, objectType);
  }, [delegate?.type, objectType]);
  const {
    creators,
    conditionOptions
  } = useMemo(() => {
    return optionsCreator?.create?.(clientProps) || {};
  }, [optionsCreator, clientProps]);
  return useMemo(() => {
    return {
      conditionCount,
      conditionOptions,
      creators,
      delegate,
      editor,
      mobile,
      onPopupChange,
      opened,
      relationship
    };
  }, [conditionCount, conditionOptions, creators, delegate, editor, mobile, onPopupChange, opened, relationship]);
}